import { useURLBlobFromUrl } from "@/hooks/useURLBlobFromUrl"
import React, { FC, useState, useRef, useEffect } from "react"

interface AudioPlayerProps {
	url: string
	fileName?: string
}

/**
 * A component for playing audio files
 */
export const AudioPlayer: FC<AudioPlayerProps> = ({ url, fileName }) => {
	const { data: blobUrl, isLoading: isBlobLoading } = useURLBlobFromUrl(url)

	const audioRef = useRef<HTMLAudioElement>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [duration, setDuration] = useState<number | null>(null)

	useEffect(() => {
		// Reset states when URL changes
		setIsLoading(true)
		setError(null)
		setDuration(null)
	}, [blobUrl])

	const handleLoadedMetadata = () => {
		setIsLoading(false)
		if (audioRef.current) {
			setDuration(audioRef.current.duration)
		}
	}

	const handleError = () => {
		setIsLoading(false)
		setError("Failed to load audio file")
	}

	// Format seconds to mm:ss
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60)
		const secs = Math.floor(seconds % 60)
		return `${mins}:${secs < 10 ? "0" : ""}${secs}`
	}

	return (
		<div className="w-full flex flex-col items-center p-4 space-y-4">
			{isLoading && (
				<div className="w-full flex justify-center items-center py-8">
					<p className="text-muted-foreground">Loading audio...</p>
				</div>
			)}

			{error && (
				<div className="w-full flex justify-center items-center py-8 text-red-500">
					<p>{error}</p>
				</div>
			)}

			<div
				className={`w-full max-w-xl rounded-md bg-muted p-4 ${
					isLoading ? "hidden" : ""
				}`}
			>
				{fileName && (
					<div className="mb-3 font-medium text-center">{fileName}</div>
				)}

				{duration && (
					<div className="text-xs text-muted-foreground mb-2 text-center">
						Duration: {formatTime(duration)}
					</div>
				)}

				<audio
					ref={audioRef}
					className="w-full"
					controls
					onLoadedMetadata={handleLoadedMetadata}
					onError={handleError}
					src={blobUrl || undefined}
				>
					Your browser does not support the audio element.
				</audio>
			</div>
		</div>
	)
}
